import React from 'react';
import { MergeCellsOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { dayAndMonthYearDate } from '@app/utils';
import { usePermissions } from '@data/hooks';
import { Namespace } from '@data/utils/enums';
import {
  AntdCol,
  AntdInput,
  AntdInputNumber,
  AntdRow,
  AntdSkeleton,
  AntdSlider,
  AntdTypography,
  Button,
} from '@ui/components';
import { QuestionsSkeleton, SuggestedQuestionsTable } from './components';
import { useQuestionsMerge } from './hooks';

const { Text } = AntdTypography;

const QuestionMerge = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const navigate = useNavigate();
  const { IS_REVIEWER } = usePermissions();

  const {
    question,
    sliderValue,
    searchString,
    questionsToShow,
    isQuestionLoading,
    isQuestionsLoading,
    debouncedSliderValue,
    handleChange,
    handleSliderChange,
    handleQuestionsMerge,
  } = useQuestionsMerge();

  const showLoading = isQuestionLoading || !question;

  return (
    <div className="pt-2 px-4 pb-8">
      <AntdRow gutter={[16, 16]}>
        <AntdCol xs={24} md={12}>
          <Text>{t('question_page.trials.question')}</Text>
          <AntdInput value={question?.text} disabled className="mt-2" />

          <AntdRow gutter={[24, 24]} className="mt-4">
            <AntdCol md={6} xs={24}>
              <Text className="block">{t('question_page.merge.form.skipped')}</Text>
              {showLoading ? (
                <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
              ) : (
                <Text className="block font-bold">{question.skippedCount}</Text>
              )}
            </AntdCol>

            <AntdCol md={6} xs={24}>
              <Text className="block">{t('question_page.merge.form.answered')}</Text>
              {showLoading ? (
                <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
              ) : (
                <Text className="block font-bold">{question.answeredCount}</Text>
              )}
            </AntdCol>

            <AntdCol md={6} xs={24}>
              <Text className="block">{t('question_page.merge.form.linked_trials')}</Text>
              {showLoading ? (
                <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
              ) : (
                <Text className="block font-bold">{question.trialCount}</Text>
              )}
            </AntdCol>

            <AntdCol md={6} xs={24}>
              <Text className="block">{t('question_page.merge.form.created_at')}</Text>
              {showLoading ? (
                <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
              ) : (
                <Text className="block font-bold">{dayAndMonthYearDate(question.createdAt)}</Text>
              )}
            </AntdCol>
          </AntdRow>

          <Text className="block mt-4">{t('question_page.merge.form.strictness')}</Text>
          <AntdRow className="text-center">
            <AntdCol span={16} offset={1}>
              <AntdSlider
                min={0}
                max={1}
                onChange={handleSliderChange}
                value={typeof sliderValue === 'number' ? sliderValue : 0}
                step={0.01}
                disabled={showLoading}
              />
            </AntdCol>
            <AntdCol span={4}>
              <AntdInputNumber
                min={0}
                max={1}
                style={{ margin: '0 16px' }}
                step={0.01}
                value={sliderValue}
                disabled={showLoading}
                onChange={handleSliderChange}
              />
            </AntdCol>
          </AntdRow>
        </AntdCol>

        <AntdCol xs={24} md={12}>
          <div className="rounded-lg bg-primary-100 p-4">
            <div className="flex items-center gap-1">
              <Text className="flex-1">{t('question_page.merge.search_title')}:</Text>
              <AntdInput
                value={searchString}
                onChange={handleChange}
                suffix={<SearchOutlined />}
                className="bg-transparent flex-1"
                placeholder={t('question_page.merge.search_placeholder')}
              />
            </div>

            {isQuestionsLoading ? (
              <QuestionsSkeleton />
            ) : (
              <ul
                style={{ borderBottomStyle: 'solid' }}
                className="list-none m-0 p-0 mt-3 border-b border-base-300"
              >
                {questionsToShow.map((q) => (
                  <li
                    key={q.id}
                    style={{ borderTopStyle: 'solid' }}
                    className="py-2 flex items-center justify-between border-t-[1px] border-base-300"
                  >
                    <Text>{q.text}</Text>
                    {!IS_REVIEWER && (
                      <>
                        <Button
                          type="text"
                          icon={<MergeCellsOutlined />}
                          className="text-primary-600 ml-auto"
                          onClick={() => handleQuestionsMerge(q.id)}
                        >
                          {t('question_page.merge.merge')}
                        </Button>
                        <Button
                          type="text"
                          icon={<SearchOutlined />}
                          className="text-primary-600"
                          onClick={() => navigate(q.id)}
                        >
                          {t('question_page.merge.check')}
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AntdCol>
      </AntdRow>

      <SuggestedQuestionsTable threshold={debouncedSliderValue} />
    </div>
  );
};

export default QuestionMerge;
